import type { Placement, UseHoverProps } from "@floating-ui/react";
import {
    arrow,
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    safePolygon,
    shift,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useMergeRefs,
    useRole,
} from "@floating-ui/react";
import { isFunction } from "@towni/common";
import * as React from "react";
import { useCallback, useRef } from "react";
import type { Except } from "type-fest";
import { TextBox } from "./text/text-box";
import { cn } from "./utils/cn";

type TooltipOptions = {
    initialOpen?: boolean;
    placement?: Placement;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
};

const useTooltip = ({
    initialOpen = false,
    placement = "top",
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    hoverOptions = {},
}: TooltipOptions & { hoverOptions?: UseHoverProps } = {}) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
    const arrowRef = useRef(null);
    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;

    const data = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(12), // Increased offset to make room for arrow
            flip({
                crossAxis: placement.includes("-"),
                fallbackAxisSideDirection: "start",
                padding: 8,
            }),
            shift({ padding: 8 }),
            arrow({
                element: arrowRef,
                padding: 5, // Add padding so the arrow doesn't get too close to the edge
            }),
        ],
    });

    const context = data.context;

    const hover = useHover(context, {
        move: false,
        handleClose: safePolygon({
            blockPointerEvents: false,
            buffer: 10,
        }),
        enabled: controlledOpen == null,
        ...hoverOptions,
    });
    const focus = useFocus(context, {
        enabled: controlledOpen == null,
    });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "tooltip" });

    const interactions = useInteractions([hover, focus, dismiss, role]);

    return React.useMemo(
        () => ({
            open,
            setOpen,
            ...interactions,
            ...data,
            arrowRef,
        }),
        [open, setOpen, interactions, data],
    );
};

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = React.createContext<ContextType>(null);

const useTooltipContext = () => {
    const context = React.useContext(TooltipContext);

    if (context == null) {
        throw new Error("Tooltip components must be wrapped in <Tooltip />");
    }

    return context;
};

/**
 * Provides tooltip context to its children, enabling tooltip functionality with customizable options.
 *
 * @param children - The React node(s) that will have tooltip behavior.
 * @param disabled - If true, disables the tooltip. Defaults to false.
 * @param delay - The delay in milliseconds before showing the tooltip on hover. Defaults to 200.
 * @param restMs - Waits until the user’s cursor is at “rest” over the reference element before changing the open state. Defaults to 0.
 * @param options - Additional tooltip options, such as placement and positioning.
 *
 * @remarks
 * This component uses the `useTooltip` hook to manage tooltip state and behavior.
 * All options are passed to `useTooltip`, with `open` and `hoverOptions` managed internally.
 *
 * @example
 * ```tsx
 * <Tooltip placement="top" delay={300}>
 *   <button>Hover me</button>
 * </Tooltip>
 * ```
 */
const Tooltip = ({
    children,
    disabled = false,
    delay = 200,
    restMs,
    ...options
}: { children: React.ReactNode } & TooltipOptions & {
        disabled?: boolean;
        /** The delay in milliseconds before showing the tooltip on hover. Defaults to 200. */
        delay?: UseHoverProps["delay"];
        /** Waits until the user’s cursor is at “rest” over the reference element before changing the open state. Defaults to 0.  */
        restMs?: UseHoverProps["restMs"];
    }) => {
    // This can accept any props as options, e.g. `placement`,
    // or other positioning options.
    const tooltip = useTooltip({
        ...options,
        open: options.open && !disabled,
        hoverOptions: { delay, enabled: !disabled, restMs },
    });
    return (
        <TooltipContext.Provider value={tooltip}>
            {children}
        </TooltipContext.Provider>
    );
};

const _TooltipTrigger = React.forwardRef<
    HTMLElement,
    React.HTMLProps<HTMLElement> & { asChild?: boolean } & {
        readonly children: React.ReactNode;
    }
>(function TooltipTrigger({ children, asChild = false, ...props }, propRef) {
    const context = useTooltipContext();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const childrenRef = (children as any).ref;
    const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

    // `asChild` allows the user to pass any element as the anchor
    if (asChild && React.isValidElement(children)) {
        return React.cloneElement(
            children,
            context.getReferenceProps({
                ref,
                ...props,
                ...(children.props ?? {}),
                ...{
                    "data-state": context.open ? "open" : "closed",
                },
            }),
        );
    }

    return (
        <div
            ref={ref}
            // The user can style the trigger based on the state
            data-state={context.open ? "open" : "closed"}
            {...context.getReferenceProps(props)}>
            {children}
        </div>
    );
});

const isChildrenText = (children: React.ReactNode): children is string => {
    return typeof children === "string";
};

const _TooltipContent = React.forwardRef<
    HTMLDivElement,
    Except<React.HTMLProps<HTMLDivElement>, "children"> & {
        readonly children:
            | React.ReactNode
            | ((actions: { closeToolTip: () => void }) => React.ReactNode);
    }
>(function TooltipContent({ style, children, ...props }, propRef) {
    const context = useTooltipContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);
    const [portalNode, setPortalNode] = React.useState<
        HTMLDivElement | undefined
    >(undefined);

    const removeTimeout = React.useRef<ReturnType<typeof setTimeout>>(null);

    // Create portal node when open, remove with delay after close
    React.useEffect(() => {
        if (context.open) {
            if (!portalNode) {
                const node = document.createElement("div");
                node.style.zIndex = Number.MAX_SAFE_INTEGER.toString();
                // node.style.pointerEvents = "none";
                node.setAttribute("data-tooltip-portal", "");
                document.body.appendChild(node);
                setPortalNode(node);
            }
            if (removeTimeout.current) {
                clearTimeout(removeTimeout.current);
            }
        } else if (portalNode) {
            removeTimeout.current = setTimeout(() => {
                if (portalNode.parentNode) {
                    portalNode.parentNode.removeChild(portalNode);
                }
                setPortalNode(undefined);
            }, 200);
        }
        return () => {
            if (removeTimeout.current) {
                clearTimeout(removeTimeout.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context.open]);

    const setOpen = context.setOpen;
    // Check if children is a function
    const isFunkis = isFunction(children);
    const childrenIsText = !isFunkis && isChildrenText(children);
    const closeToolTip = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    if (!context.open || !portalNode) return null;
    if (!children) return null;

    return (
        <FloatingPortal root={portalNode}>
            <div
                ref={ref}
                className={cn(
                    "z-20 overflow-visible rounded-lg border-1 border-neutral-100 bg-white px-4 py-2 shadow-md",
                    props.className,
                )}
                style={{
                    ...context.floatingStyles,
                    ...style,
                }}
                {...context.getFloatingProps(props)}>
                {/* Main content */}
                {isFunkis ? (
                    children({ closeToolTip })
                ) : childrenIsText ? (
                    <TextBox text={children} />
                ) : (
                    children
                )}

                {/* Arrow element with more prominent styling */}
                {/* <FloatingArrow
                    ref={context.arrowRef}
                    context={context.context}
                    className="size-5 fill-white [&>path:first-of-type]:stroke-neutral-100 [&>path:last-of-type]:stroke-white"
                    strokeWidth={1}
                    tipRadius={2}
                    style={{
                        visibility: middlewareData.arrow ? "visible" : "hidden",
                    }}
                /> */}
            </div>
        </FloatingPortal>
    );
});

Tooltip.Trigger = _TooltipTrigger;
Tooltip.Content = _TooltipContent;

export {
    Tooltip,
    _TooltipContent as TooltipContent,
    _TooltipTrigger as TooltipTrigger,
};
export type { TooltipOptions };
